import { Chip } from '@design/components/chip/Chip';
import { DropdownMenu } from '@design/components/dropdown-menu/DropdownMenu';
import { Icon } from '@design/components/icon/Icon';
import { TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { SvgIconNames } from '@design/design-tokens/iconography/SvgIcons';
import { Option } from '@terminal/components/filter/Option';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { FilterOption } from '@terminal/components/update-filter-options/updateFilterOptions';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useReasonerParams } from '@terminal/views/alfred/research/hooks/useReasonerParams';
import { SubmittedAnalysisMode } from '@terminal/views/alfred/research/services/conversations-service';
import { useChat } from '@terminal/views/alfred/research/use-chat/useChat';
import clsx from 'clsx';
import { RefObject, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AnalysisModeDropdown.module.scss';

interface DropdownOption {
  key: string;
  icon: SvgIconNames;
  name: string;
  value: {
    analysis_mode: SubmittedAnalysisMode;
  };
  description: string;
}

export interface AnalysisModeDropdownProps {
  isMessagePending: boolean;
  contentRef: RefObject<HTMLDivElement | null>;
}

export const AnalysisModeDropdown = ({
  isMessagePending,
  contentRef,
}: AnalysisModeDropdownProps) => {
  const { t } = useTranslation(['chat']);

  const { analysisMode, webSearch, activeConversation, setAnalysisMode } =
    useChat(state => ({
      analysisMode: state.analysisMode,
      webSearch: state.webSearch,
      activeConversation: state.activeConversation,
      setAnalysisMode: state.setAnalysisMode,
    }));
  const { conversationId } = useReasonerParams();
  const { trackAlfredDeepSelected } = usePosthogEvents();

  const isAlfredAskAnalystEnabled = useFlags(state =>
    state.getFeatureFlagState('alfred_ask_analyst')
  );

  const renderCustomFilterOptionLabel = (
    filterOption: FilterOption,
    disabled: boolean
  ) => {
    return (
      <div className={styles.optionWrapper}>
        {filterOption.icon && (
          <Icon
            iconName={filterOption.icon}
            size={16}
            fillColor={
              disabled ? 'var(--text-disabled)' : 'var(--text-default)'
            }
          />
        )}
        <span className={styles.label}>{filterOption.name}</span>
      </div>
    );
  };

  const options: DropdownOption[] = isAlfredAskAnalystEnabled
    ? [
        {
          key: 'auto',
          icon: 'LightningBolt',
          name: t('chat:analysisMode.askTitle'),
          value: {
            analysis_mode: 'Auto',
          },
          description: t('chat:analysisMode.askDescription'),
        },
        {
          key: 'deep',
          icon: 'FileSearch',
          name: t('chat:analysisMode.analystTitle'),
          value: {
            analysis_mode: 'Deep',
          },
          description: t('chat:analysisMode.analystDescription'),
        },
      ]
    : [
        {
          key: 'auto',
          icon: 'QuickAnalysis',
          name: t('chat:analysisMode.autoTitle'),
          value: {
            analysis_mode: 'Auto',
          },
          description: t('chat:analysisMode.autoDescription'),
        },
        {
          key: 'deep',
          icon: 'Alfred',
          name: t('chat:analysisMode.deepTitle'),
          value: {
            analysis_mode: 'Deep',
          },
          description: t('chat:analysisMode.deepDescription'),
        },
      ];

  const selectedOption = options.find(
    i => i.value.analysis_mode === analysisMode
  );

  const forceToDeep = isAlfredAskAnalystEnabled ? false : webSearch === 'block';

  useEffect(() => {
    if (forceToDeep) {
      setAnalysisMode('Deep');
    } else if (!conversationId && isAlfredAskAnalystEnabled) {
      setAnalysisMode('Auto');
    }

    if (activeConversation && activeConversation.tasks) {
      const lastTask = activeConversation.tasks.at(-1);
      if (lastTask) {
        if (lastTask.submittedAnalysisMode) {
          setAnalysisMode(lastTask.submittedAnalysisMode);
        } else {
          setAnalysisMode(lastTask.analysis_mode === 'Deep' ? 'Deep' : 'Auto');
        }
      }
    }
  }, [webSearch, conversationId, activeConversation]);

  return (
    <DropdownMenu
      className={styles.styledDropdownMenu}
      contentRef={contentRef}
      triggerProps={{
        disabled: isMessagePending,
      }}
      triggerNode={
        <div>
          <Chip
            className={styles.styledChip}
            label={selectedOption?.name}
            leftIcon={selectedOption?.icon}
            rightIcon="ChevronLightDown"
            rightIconSize={8}
            disabled={isMessagePending}
            data-testid="analysis-mode-dropdown-trigger"
            prefixNode={
              isAlfredAskAnalystEnabled ? null : (
                <span className={styles.prefixText}>
                  {t('chat:analysisMode.prefix')}
                </span>
              )
            }
          />
        </div>
      }
    >
      {options.map(i => {
        const autoDisabled = forceToDeep && i.value.analysis_mode === 'Auto';

        return (
          <TooltipContainer
            key={i.key}
            label={
              isAlfredAskAnalystEnabled
                ? t('chat:analysisMode.enableAsk')
                : t('chat:analysisMode.enableAuto')
            }
            trigger={[TooltipTrigger.Hover]}
            inPortal
            disabled={!autoDisabled}
          >
            <div
              className={clsx({
                [styles.styledOptionWrapperDisabled]: autoDisabled,
              })}
            >
              <Option
                option={i}
                isChecked={analysisMode === i.value.analysis_mode}
                disabled={autoDisabled}
                indentedDescription={24}
                handleClick={() => {
                  setAnalysisMode(i.value.analysis_mode);

                  if (i.value.analysis_mode === 'Deep') {
                    trackAlfredDeepSelected(POSTHOG_TRACKING_SOURCES.ALFRED);
                  }
                }}
                renderCustomFilterOptionLabel={() =>
                  renderCustomFilterOptionLabel(i, autoDisabled)
                }
              />
            </div>
          </TooltipContainer>
        );
      })}
    </DropdownMenu>
  );
};
